// CLIENT PORTAL §7 — WHICH REPORT CONTROLS LIVE IN THE URL, AND WHAT SURVIVES A
// TAB CHANGE (2026-09-03). Pure, so both rules are testable without a router.
//
// ⚠️⚠️ THE DEFECT THIS FIXES WAS TWO DEFECTS. A tab change navigated to a BARE
// PATH, so `?account=` and `?accounts=` were dropped — Scott's symptom, the club
// falling back to All locations. But `useReportState()` is called ONCE PER ROUTE
// COMPONENT, and the period, comparison and grouping were LOCAL REACT STATE, so
// the route change also mounted a fresh hook and reset all three to their
// defaults. Scott reported both halves independently; he noticed the club first
// because it is the only one of them the page NAMES.
//
// ⚠️ So carrying the search string alone is NOT the fix — the period has to
// actually be in the URL for there to be anything to carry. It is also the
// better place on its own merits: "look at August vs last year for Kalgoorlie"
// becomes one forwardable link, it survives a refresh and the back button, and
// the react-query key becomes correct by construction because the key IS the
// resolved params.

import { PORTAL_COMPARISON_MODES } from './comparison.js';
import type { PortalComparisonMode, PortalGrouping } from '../../types/portalReport.js';
import type { ReportingDatePreset } from '../../types/reporting.js';

/** ⚠️ Shared with `portalReportHref` in portalNav.ts — the nav writes what this reads. */
export const ACCOUNT_PARAM = 'account';
/** The MULTI-select's parameter (§9). The server checks this one FIRST. */
export const ACCOUNTS_PARAM = 'accounts';
export const PRESET_PARAM = 'preset';
export const FROM_PARAM = 'from';
export const TO_PARAM = 'to';
export const COMPARISON_PARAM = 'comparison';
export const GROUPING_PARAM = 'grouping';

/**
 * ⚠️⚠️ AN ALLOWLIST, DENY BY DEFAULT — the parameters that survive a change of
 * channel tab.
 *
 * The scope (`account` / `accounts`) is channel-independent, and the period,
 * comparison and grouping are a VIEW OF THE SAME WINDOW, so all of them carry.
 *
 * ⚠️⚠️ AND `campaignIds` IS NOT HERE, WHICH IS THE POINT. Meta campaign ids and
 * Google campaign ids are DIFFERENT ID SPACES. Carry a Meta campaign filter to
 * the Google tab and the server filters Google campaigns by ids that exist
 * nowhere in Google — a SILENTLY EMPTY report, because "filtered to nothing" is
 * a legitimate state the code deliberately honours (`empty ≠ absent`). A blunt
 * "preserve the whole search string" fixes Scott's bug and introduces that one.
 *
 * ⚠️ DENY-BY-DEFAULT IS THE STRUCTURE, not the specific omission: a parameter
 * added later is dropped on a tab change until somebody deliberately lists it,
 * and for a channel-specific one that is the safe direction. An exclusion list
 * would default the other way.
 */
export const TAB_CHANGE_PARAMS: readonly string[] = [
  ACCOUNT_PARAM,
  ACCOUNTS_PARAM,
  PRESET_PARAM,
  FROM_PARAM,
  TO_PARAM,
  COMPARISON_PARAM,
  GROUPING_PARAM,
];

/**
 * The search string a channel tab change should land on.
 *
 * ⚠️ Returns the SEARCH STRING (with a leading `?`, or empty), so the caller can
 * hand it straight to `navigate` — the shape that makes the call site read as
 * "this path, these params" rather than as URL assembly.
 */
export function carryAcrossTabChange(prev: URLSearchParams): string {
  const next = new URLSearchParams();
  for (const key of TAB_CHANGE_PARAMS) {
    const value = prev.get(key);
    // ⚠️ `!== null`, not truthiness: `?accounts=` PRESENT AND EMPTY means
    // "filtered to nothing", which is a real state and must survive a tab
    // change like any other. Dropping it would silently widen the report.
    if (value !== null) next.set(key, value);
  }
  const q = next.toString();
  return q ? `?${q}` : '';
}

/**
 * ⚠️ A URL is user-editable and forwardable, so every value read from one is
 * validated against its own vocabulary. An unrecognised value falls back to the
 * default rather than reaching the server or rendering an empty control — a
 * stale bookmark from a renamed preset must not break the page.
 */
export function parsePreset(
  raw: string | null,
  known: ReadonlyArray<{ value: ReportingDatePreset }>,
  fallback: ReportingDatePreset,
): ReportingDatePreset {
  return known.some((p) => p.value === raw) ? (raw as ReportingDatePreset) : fallback;
}

export function parseComparison(raw: string | null): PortalComparisonMode {
  return PORTAL_COMPARISON_MODES.some((m) => m.value === raw)
    ? (raw as PortalComparisonMode)
    : // ⚠️⚠️ `lastYear` IS THE DEFAULT, and its being the default is exactly why
      // §7's own test has to start from a NON-default comparison: a reset to
      // `lastYear` looks like no change at all, so a test that starts there
      // passes over the bug. The same trap as the round-4 router mocks that
      // stayed green because they ignored the parameter.
      'lastYear';
}

export function parseGrouping(raw: string | null): PortalGrouping {
  return raw === 'monthly' || raw === 'daily' ? raw : 'daily';
}

/**
 * ⚠️ A CUSTOM RANGE END IS OMITTED UNTIL IT EXISTS, never written empty.
 *
 * The calendar decides "extend this range" vs "start over" by whether it already
 * has an END, so a placeholder end makes the second click restart the selection
 * and the reader can never finish a range. `useReportState` documents the same
 * trap from the state side; putting the range in the URL must not reintroduce it.
 */
export function customRangeParams(
  range: { from?: string | undefined; to?: string | undefined },
): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (range.from) out.from = range.from;
  if (range.to) out.to = range.to;
  return out;
}
